import React from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Button } from '../../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardFooter, CardDescription } from '../../components/ui/card';
import { api, useQuery, useMutation } from '../../lib/convex';
import type { Id } from '../../lib/convex';
import { TeacherAuth, SignOut } from '../../components/auth/TeacherAuth';

const TeacherDashboardPage: React.FC = () => {
  return (
    <TeacherAuth>
      <TeacherDashboardContent />
    </TeacherAuth>
  );
};

const TeacherDashboardContent: React.FC = () => {
  const navigate = useNavigate();
  
  // Fetch all quizzes
  const quizzes = useQuery(api.quizzes.list) || [];

  // Query all sessions and filter for active ones
  const allSessions = useQuery(api.sessions.listAll) || [];
  const sessions = allSessions.filter(
    s => s.status === 'active' || s.status === 'waiting'
  );
  
  const createSession = useMutation(api.sessions.create);
  const deleteQuiz = useMutation(api.quizzes.remove);

  const handleStartSession = async (quizId: Id<"quizzes">) => {
    try {
      const sessionId = await createSession({ quizId });
      
      navigate({ 
        to: `/teacher/sessions/${sessionId}` 
      });
    } catch (error) {
      console.error('Error creating session:', error);
      alert('Failed to create session. Please try again.');
    }
  };

  const handleDeleteQuiz = async (quizId: Id<"quizzes">, quizTitle: string) => {
    const activeSessions = allSessions.filter(
      s => s.quizId === quizId && 
      (s.status === 'active' || s.status === 'waiting')
    );
    
    if (activeSessions.length > 0) {
      alert(
        `Cannot delete "${quizTitle}" because it has ${activeSessions.length} active session(s).\n\n` +
        'Please end the session(s) first.'
      );
      return;
    }
    
    const allRelatedSessions = allSessions.filter(s => s.quizId === quizId);
    
    let message = `Are you sure you want to delete "${quizTitle}"?`;
    
    if (allRelatedSessions.length > 0) {
      message += `\n\nThis quiz has ${allRelatedSessions.length} completed session(s). All session data will also be deleted.`;
    }
    
    message += '\n\nThis action cannot be undone.';
    
    const confirmed = confirm(message);
    
    if (!confirmed) return;
    
    try {
      await deleteQuiz({ 
        id: quizId,
        deleteRelatedSessions: allRelatedSessions.length > 0
      });
    } catch (error) {
      console.error('Error deleting quiz:', error);
      alert('Failed to delete quiz. Please try again.');
    }
  };

  if (quizzes === undefined || allSessions === undefined) {
    return (
      <div className="container max-w-6xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container max-w-6xl mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-heading font-bold">Teacher Dashboard</h1>
        <div className="flex gap-2">
          <Link to="/">
            <Button variant="outline">Home</Button>
          </Link>
          <SignOut />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Quizzes Section */}
        <div className="md:col-span-2 space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-heading font-bold">My Quizzes</h2>
            <Link to="/teacher/quizzes/new">
              <Button>Create New Quiz</Button>
            </Link>
          </div>

          {quizzes.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <p className="text-muted-foreground mb-4">
                  You haven't created any quizzes yet
                </p>
                <Link to="/teacher/quizzes/new">
                  <Button>Create Your First Quiz</Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {quizzes.map((quiz) => (
                <Card key={quiz._id}>
                  <CardHeader>
                    <CardTitle>{quiz.title}</CardTitle>
                    <CardDescription>
                      {quiz.questions.length} questions
                    </CardDescription>
                  </CardHeader>
                  <CardFooter className="justify-between">
                    <div className="flex space-x-2">
                      <Link to={`/teacher/quizzes/${quiz._id}`}>
                        <Button variant="outline" size="sm">
                          Edit
                        </Button>
                      </Link>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleStartSession(quiz._id)}
                      >
                        Start Session
                      </Button>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-destructive"
                      onClick={() => handleDeleteQuiz(quiz._id, quiz.title)}
                    >
                      Delete
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Sessions and Stats Section */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Active Sessions</CardTitle>
            </CardHeader>
            <CardContent>
              {sessions.length === 0 ? (
                <p className="text-muted-foreground">
                  No active sessions
                </p>
              ) : (
                <div className="space-y-2">
                  {sessions.map((session) => {
                    const quiz = quizzes.find(q => q._id === session.quizId);
                    
                    return (
                      <div key={session._id} className="flex flex-col p-2 border rounded gap-1">
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="font-medium">{quiz?.title || 'Unknown Quiz'}</span>
                          </div>
                          <Link to={`/teacher/sessions/${session._id}`}>
                            <Button size="sm" variant="outline">
                              Control
                            </Button>
                          </Link>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Code: <span className="font-mono font-bold">{session.code}</span> • 
                          Status: {session.status}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Quick Stats</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Total Quizzes</span>
                  <span className="font-bold">{quizzes.length}</span>
                </div>
                <div className="flex justify-between">
                  <span>Total Questions</span>
                  <span className="font-bold">
                    {quizzes.reduce((total, quiz) => total + quiz.questions.length, 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Active Sessions</span>
                  <span className="font-bold">{sessions.length}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TeacherDashboardPage;